import { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Plus, Settings, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useApiClient } from "@/lib/api-client";
import { useAuthContext } from "@/components/auth/ClerkAuthProvider";
import type { Tenant } from "@/types/firestore";

interface CreditBalanceWidgetProps {
  tenant: Tenant;
}

export function CreditBalanceWidget({ tenant }: CreditBalanceWidgetProps) {
  const [isLoading, setIsLoading] = useState(false);
  const api = useApiClient();
  const { isSuperAdmin } = useAuthContext();

  // Credit breakdown
  const monthlyCredits = tenant.generation_credits_monthly ?? 0;
  const addonCredits = tenant.generation_credits_addon ?? 0;
  const rolloverCredits = tenant.generation_credits_rollover ?? 0;
  const totalCredits = monthlyCredits + addonCredits + rolloverCredits;
  const creditsCap = tenant.credits_cap ?? 50;

  // Calculate usage percentage
  const usagePercentage = creditsCap > 0 ? (totalCredits / creditsCap) * 100 : 0;

  // Status color based on remaining credits
  const getStatusColor = () => {
    if (totalCredits === 0) return "destructive";
    if (totalCredits <= 2) return "warning";
    return "default";
  };

  const handleBuyCredits = async () => {
    setIsLoading(true);
    try {
      const data = await api.post<{ sessionId: string; url: string }>(
        "/stripe/buy-credits",
        {}
      );

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      toast.error("Failed to start checkout", {
        description: "Please try again or contact support if the issue persists.",
      });
      setIsLoading(false);
    }
  };

  return (
    <Card className="border-border/40 bg-card shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Generation Credits
          </CardTitle>
          <Badge variant={isSuperAdmin ? "default" : getStatusColor()} className="text-xs">
            {isSuperAdmin ? "Unlimited" : `${totalCredits} remaining`}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isSuperAdmin ? (
          <div className="py-6 text-center">
            <p className="text-sm text-muted-foreground">
              Super admin accounts have unlimited credits
            </p>
          </div>
        ) : (
          <>
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Usage</span>
                <span className="font-medium tabular-nums">
                  {totalCredits} / {creditsCap}
                </span>
              </div>
              <Progress value={usagePercentage} className="h-2" />
            </div>

            {/* Credit Breakdown */}
            <div className="space-y-2 pt-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Monthly</span>
                <span className="font-medium tabular-nums">{monthlyCredits}</span>
              </div>
              {addonCredits > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Addon</span>
                  <span className="font-medium tabular-nums text-primary">{addonCredits}</span>
                </div>
              )}
              {rolloverCredits > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Rollover</span>
                  <span className="font-medium tabular-nums text-accent">{rolloverCredits}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="pt-2 space-y-2">
              <Button
                onClick={handleBuyCredits}
                disabled={isLoading}
                className="w-full gap-2"
                size="sm"
              >
                <Plus className="h-4 w-4" />
                {isLoading ? "Loading..." : "Buy More Credits"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2"
                asChild
              >
                <a href="/settings">
                  <Settings className="h-4 w-4" />
                  Manage Plan
                </a>
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
